import { post } from "@src/appV2/api/parsedApi";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { GET_FACILITY_DAY_SHIFTS_URL } from "./queryKeys";

const NO_CALL_NO_SHOW_REASON_TYPE = "NO_CALL_NO_SHOW";

const reportNoShowBodySchema = z.object({
  shiftId: z.string(),
  agentId: z.string(),
  sessionUser: z.string(),
  isAgentCancel: z.literal(false),
  isNoCallNoShow: z.literal(true),
  reasonType: z.literal(NO_CALL_NO_SHOW_REASON_TYPE),
  reasonDescription: z.string(),
  requestReplacement: z.boolean(),
});

const reportNoShowResponseSchema = z.unknown();

export interface ReportNoShowParams {
  shiftId: string;
  workerId: string;
  userId: string;
  reasonDescription: string;
  requestReplacement: boolean;
}

/**
 * Reports a no-call-no-show by unassigning the worker from the shift.
 * Uses the /shift/unassign endpoint with isNoCallNoShow=true.
 * Invalidates day shifts cache on success.
 */
export function useReportNoShow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ReportNoShowParams) => {
      await post({
        url: "/shift/unassign",
        data: {
          shiftId: params.shiftId,
          agentId: params.workerId,
          sessionUser: params.userId,
          isAgentCancel: false,
          isNoCallNoShow: true,
          reasonType: NO_CALL_NO_SHOW_REASON_TYPE,
          reasonDescription: params.reasonDescription,
          requestReplacement: params.requestReplacement,
        },
        bodySchema: reportNoShowBodySchema,
        responseSchema: reportNoShowResponseSchema,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [GET_FACILITY_DAY_SHIFTS_URL],
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.REPORT_NO_SHOW_FAILURE,
    },
  });
}
